import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ArrowLeft, CalendarIcon, Building2, DollarSign, ChevronRight } from "lucide-react";
import { NestIcon } from "../components/NestIcon";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

export default function QuoteRequest() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [formData, setFormData] = useState({
    monthly_rent: "",
    lease_start_date: null,
    lease_end_date: null,
    employer_name: "",
    employment_start_date: null,
    annual_income: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/quotes/request`, {
        monthly_rent: parseFloat(formData.monthly_rent),
        lease_start_date: formData.lease_start_date ? format(formData.lease_start_date, "yyyy-MM-dd") : "",
        lease_end_date: formData.lease_end_date ? format(formData.lease_end_date, "yyyy-MM-dd") : "",
        employer_name: formData.employer_name,
        employment_start_date: formData.employment_start_date ? format(formData.employment_start_date, "yyyy-MM-dd") : "",
        annual_income: parseFloat(formData.annual_income)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setQuote(response.data);
      toast.success("Quote generated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate quote");
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    if (!quote) return;

    try {
      const response = await axios.post(`${API}/payments/checkout/session`, {
        quote_id: quote.quote_id,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate payment");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <NestIcon className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                TheRentPayer
              </span>
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost">Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
            Get Your Quote
          </h1>
          <p className="text-muted-foreground">
            Tell us about your rental and we'll calculate your premium
          </p>
        </div>

        {!quote ? (
          <Card className="card-organic">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Rental Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
                    Rental Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="monthly_rent">Monthly Rent ($)</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="monthly_rent"
                          type="number"
                          placeholder="1500"
                          value={formData.monthly_rent}
                          onChange={(e) => setFormData({ ...formData, monthly_rent: e.target.value })}
                          className="pl-10"
                          required
                          data-testid="quote-rent-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Lease Start Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="lease-start-date-btn">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.lease_start_date ? format(formData.lease_start_date, "PPP") : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.lease_start_date}
                            onSelect={(date) => setFormData({ ...formData, lease_start_date: date })}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Lease End Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="lease-end-date-btn">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.lease_end_date ? format(formData.lease_end_date, "PPP") : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.lease_end_date}
                            onSelect={(date) => setFormData({ ...formData, lease_end_date: date })}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                {/* Employment Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
                    Employment Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="employer_name">Employer Name</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="employer_name"
                          type="text"
                          placeholder="Company Inc."
                          value={formData.employer_name}
                          onChange={(e) => setFormData({ ...formData, employer_name: e.target.value })}
                          className="pl-10"
                          required
                          data-testid="employer-name-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Employment Start Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="employment-start-date-btn">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.employment_start_date ? format(formData.employment_start_date, "PPP") : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.employment_start_date}
                            onSelect={(date) => setFormData({ ...formData, employment_start_date: date })}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="annual_income">Annual Income ($)</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="annual_income"
                          type="number"
                          placeholder="60000"
                          value={formData.annual_income}
                          onChange={(e) => setFormData({ ...formData, annual_income: e.target.value })}
                          className="pl-10"
                          required
                          data-testid="annual-income-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full py-6"
                  disabled={loading}
                  data-testid="submit-quote-btn"
                >
                  {loading ? "Calculating..." : "Get My Quote"}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-organic animate-fade-in">
            <CardHeader className="text-center pb-0">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <NestIcon className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Young Serif, serif' }}>
                Your Quote is Ready!
              </CardTitle>
              <CardDescription>
                Quote ID: {quote.quote_id}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              <div className="calculator-result mb-6">
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-white/70 text-sm">Monthly Rent</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Young Serif, serif' }}>
                      ${quote.monthly_rent}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Monthly Premium</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Young Serif, serif' }}>
                      ${quote.monthly_premium}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-white/70 text-sm">One-time Signup Fee</p>
                    <p className="text-xl font-bold text-white">${quote.signup_fee}</p>
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Coverage Duration</p>
                    <p className="text-xl font-bold text-white">{quote.coverage_months} months</p>
                  </div>
                </div>
                <div className="border-t border-white/20 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white/90">First Payment Total</span>
                    <span className="text-3xl font-bold text-white" style={{ fontFamily: 'Young Serif, serif' }}>
                      ${quote.total_first_payment}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-muted rounded-lg p-4 mb-6">
                <h4 className="font-semibold mb-2">What's included:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>✓ Up to 3 months of rent coverage</li>
                  <li>✓ Quick claim processing</li>
                  <li>✓ Direct payments to your landlord</li>
                  <li>✓ 24/7 customer support</li>
                </ul>
              </div>

              <div className="flex gap-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => navigate("/dashboard")}
                >
                  Save for Later
                </Button>
                <Button 
                  className="btn-primary flex-1"
                  onClick={handlePayNow}
                  data-testid="pay-now-btn"
                >
                  Pay Now
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              <p className="text-center text-xs text-muted-foreground mt-4">
                Quote expires on {new Date(quote.expires_at).toLocaleDateString()}
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
